import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ComponentLoader } from "@/components/loader";
import { BackButton } from "@/components/button";
import { useAppState } from "@/modules/app/useAppContext";
import { PermissionsEnum } from "@/helpers/constants";
import { getDbtCloudConnection } from "@/helpers/apis";
import { DBT_CLOUD_QUERY_KEYS } from "../../types";
import DbtCloudConnection from "./DbtCloudConnection";
import SyncHistory from "./SyncHistory";
import { Stack } from "@altimateai/lego";
import { Typography } from "@altimateai/lego";
import { IconChevronRight } from "@tabler/icons-react";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import ColumnPickerProvider from "@/components/column/ColumnPickerProvider";

const DbtCloudDetails = (): JSX.Element => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const { loggedinUser: user } = useAppState();

  const { data: connection, isLoading } = useQuery({
    queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTION, connectionId],
    queryFn: () => getDbtCloudConnection(Number(connectionId)),
    enabled: !!connectionId,
  });

  if (isLoading) return <ComponentLoader left={60} label="Loading connection details..." />;

  if (!connection) return <></>;

  return (
    <ColumnPickerProvider>
      <DatatableContextProvider>
        <Stack direction="column" className="al-overflow-y-auto al-h-full">
          <Stack direction="row" align="center" gap="sm">
            <BackButton defaultUrl="/settings/datastores" />
            <Stack direction="row" align="center" gap="xs" className="al-font-semibold al-text-lg">
              <Link to="/settings/datastores" className="al-text-muted-foreground">
                Connections
              </Link>
              <IconChevronRight size={16} className="al-text-muted-foreground" />
              <Typography weight="regular">{connection.name}</Typography>
            </Stack>
          </Stack>
          <Stack direction="column" gap="md" className="al-mt-4">
            <DbtCloudConnection
              connection={connection}
              syncOpAllowed={Boolean(user?.permission?.includes(PermissionsEnum.DATASTORE_EDIT))}
            />
            <SyncHistory connectionId={connection.id} />
          </Stack>
        </Stack>
      </DatatableContextProvider>
    </ColumnPickerProvider>
  );
};

export default DbtCloudDetails;
